import type { RefreshRun } from "./api";

export const POLL_MS = 10_000;
export const FOLLOW_CAP_MS = 30 * 60_000;

export const COPY = {
  refresh: "Refresh data",
  starting: "Starting…",
  refreshing: "Refreshing…",
  running: "Refreshing data… started",
  already: "A rebuild is already running",
  following: "Following it.",
  waiting: "Still waiting for the rebuild…",
  done: "Data rebuilt at",
  failed: "The rebuild failed. The previous data is still shown.",
  forbidden: "You need edit access to the sourcing area to refresh data.",
  refused: "Refresh could not start. Try again in a minute.",
  cap: "Still rebuilding. Check back later.",
} as const;

export type RefreshState = "idle" | "starting" | "running" | "done" | "failed" | "refused" | "cap";

export type RefreshModel = {
  state: RefreshState;
  pressAt: number | null;
  followStart: number | null;
  runId: string | null;
  baselineRunId: string | null;
  followedRunId: string | null;
  startedAt: string | null;
  builtAt: string | null;
  alreadyRunning: boolean;
  pollFailed: boolean;
  refusedStatus: number | null;
};

export const IDLE: RefreshModel = {
  state: "idle",
  pressAt: null,
  followStart: null,
  runId: null,
  baselineRunId: null,
  followedRunId: null,
  startedAt: null,
  builtAt: null,
  alreadyRunning: false,
  pollFailed: false,
  refusedStatus: null,
};

export type FollowVerdict = "cap" | "waiting" | "ok" | "failed" | "follow";

export function followVerdict({
  run,
  failed,
  now,
  followStart,
  runId,
  baselineRunId,
}: {
  run: RefreshRun | null;
  failed: boolean;
  now: number;
  followStart: number;
  runId: string | null;
  baselineRunId: string | null;
}): FollowVerdict {
  if (now - followStart >= FOLLOW_CAP_MS) return "cap";
  if (failed) return "waiting";
  const isFollowed = run !== null && (
    runId !== null ? run.run_id === runId : run.run_id !== baselineRunId
  );
  if (isFollowed && run.status !== "running") return run.status === "ok" ? "ok" : "failed";
  return "follow";
}

export type RefreshEvent =
  | { type: "press"; now: number }
  | { type: "started"; baselineRunId: string | null }
  | { type: "already"; run: RefreshRun }
  | { type: "attach"; run: RefreshRun; now: number }
  | { type: "refused"; status: number }
  | { type: "poll"; run: RefreshRun | null; failed: boolean; now: number };

export function refreshReducer(model: RefreshModel, event: RefreshEvent): RefreshModel {
  switch (event.type) {
    case "press":
      return {
        ...IDLE,
        state: "starting",
        pressAt: event.now,
        followedRunId: model.followedRunId,
      };
    case "started":
      if (model.state !== "starting") return model;
      return {
        ...model,
        state: "running",
        followStart: model.pressAt,
        baselineRunId: event.baselineRunId,
      };
    case "already":
      if (model.state !== "starting") return model;
      return {
        ...model,
        state: "running",
        followStart: model.pressAt,
        runId: event.run.run_id,
        followedRunId: event.run.run_id,
        startedAt: event.run.started_at,
        alreadyRunning: true,
      };
    case "attach":
      if (model.state !== "idle" || event.run.status !== "running" || event.run.run_id === model.followedRunId) return model;
      return {
        ...model,
        state: "running",
        followStart: event.now,
        runId: event.run.run_id,
        followedRunId: event.run.run_id,
        startedAt: event.run.started_at,
        alreadyRunning: true,
      };
    case "refused":
      return { ...model, state: "refused", refusedStatus: event.status };
    case "poll": {
      if (model.state !== "running" || model.followStart === null) return model;
      const verdict = followVerdict({
        run: event.run,
        failed: event.failed,
        now: event.now,
        followStart: model.followStart,
        runId: model.runId,
        baselineRunId: model.baselineRunId,
      });
      if (verdict === "cap") return { ...model, state: "cap", pollFailed: false };
      if (verdict === "waiting") return { ...model, pollFailed: true };

      const isCurrent = event.run !== null && (
        model.runId !== null ? event.run.run_id === model.runId : event.run.run_id !== model.baselineRunId
      );
      const startedAt = isCurrent && event.run?.status === "running" ? event.run.started_at : model.startedAt;
      const followedRunId = isCurrent && event.run ? event.run.run_id : model.followedRunId;
      if (verdict === "ok") {
        return { ...model, state: "done", followedRunId, builtAt: event.run?.built_at ?? null, pollFailed: false };
      }
      if (verdict === "failed") {
        return { ...model, state: "failed", followedRunId, pollFailed: false };
      }
      return { ...model, followedRunId, startedAt, pollFailed: false };
    }
  }
}
